import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import Toast from "../components/Toast";
import {useConfirmDialog} from "../components/ConfirmDialogProvider";
import {
  AdminEmptyState,
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { useAdminExamPins } from "../store/admin";
import {
  EXAM_TYPE_OPTIONS,
  formatDate,
  pinLabel,
  statusLabel,
  statusBadgeClass,
  examTypeLabel,
  examTypeBadgeClass,
  resolvePinPrice,
} from "./components/examPins/examPinUtils";

const PRICE_TYPES = EXAM_TYPE_OPTIONS.filter((o) => o.value);

// ===== EXAM PIN PRICE & PIN MANAGEMENT =====
// GET/PATCH /api/admin/exam-pins/price, DELETE /api/admin/exam-pins/:id.
const ExamPinPrice = () => {
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const {
    fetchExamPinPrice,
    updateExamPinPrice,
    examPins = [],
    examPinsLoading,
    fetchExamPins,
    deleteExamPin,
    examPinMutationLoading,
  } = useAdminExamPins();

  const [examType, setExamType] = useState("WAEC");
  const [priceInput, setPriceInput] = useState("");
  const [priceSaving, setPriceSaving] = useState(false);
  const [prices, setPrices] = useState({});

  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);
  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  // GET /api/admin/exam-pins/price?examType=…
  const loadSelectedPrice = useCallback(
    async (type) => {
      const res = await fetchExamPinPrice({ examType: type });
      if (res.success) setPriceInput(resolvePinPrice(res.price, type));
    },
    [fetchExamPinPrice]
  );

  // GET the price for every exam type (summary cards).
  const loadAllPrices = useCallback(async () => {
    const entries = await Promise.all(
      PRICE_TYPES.map(async (t) => {
        const res = await fetchExamPinPrice({ examType: t.value });
        return [t.value, res.success ? resolvePinPrice(res.price, t.value) : ""];
      })
    );
    setPrices(Object.fromEntries(entries));
  }, [fetchExamPinPrice]);

  useEffect(() => {
    loadSelectedPrice(examType);
  }, [loadSelectedPrice, examType]);

  useEffect(() => {
    loadAllPrices();
  }, [loadAllPrices]);

  const refreshPins = useCallback(() => {
    fetchExamPins({ page: 1, limit: 10, sort: "newest" });
  }, [fetchExamPins]);

  useEffect(() => {
    refreshPins();
  }, [refreshPins]);

  // PATCH /api/admin/exam-pins/price
  const handleSavePrice = async (e) => {
    e.preventDefault();
    const numeric = Number(String(priceInput).replace(/,/g, ""));
    if (!Number.isFinite(numeric) || numeric <= 0) {
      showToast("Enter a valid price", false);
      return;
    }
    setPriceSaving(true);
    const res = await updateExamPinPrice({ price: numeric, examType });
    setPriceSaving(false);
    if (res.success) {
      showToast(`${examType} PIN price updated`, true);
      await Promise.all([loadSelectedPrice(examType), loadAllPrices()]);
    } else {
      showToast(res.error || "Failed to update PIN price", false);
    }
  };

  // DELETE /api/admin/exam-pins/:id
  const handleDelete = async (pin) => {
    return confirm({
      title: `Delete pin ${pinLabel(pin)}?`,
      description: "This action cannot be undone.",
      confirmLabel: "Delete pin",
      onConfirm: async () => {
        const res = await deleteExamPin(pin.id);
        if (!res.success) throw new Error(res.error || "Failed to delete exam pin.");
        showToast("Exam pin deleted", true);
        refreshPins();
      },
    });
  };

  return (
    <div>
      <AdminNavBar
        title="Exam PIN Price"
        subTitle="Set prices & manage pins"
        context="Update exam PIN prices and manage the related pin records."
      />
      <AdminPageBody>
        <button
          onClick={() => navigate("/admin/exam-pins")}
          className="inline-flex w-fit items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Exam Pins
        </button>

        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}

        {/* Price management */}
        <AdminSectionHeader
          title="Exam PIN Price"
          description="Set the current price for each exam type."
        />

        <AdminTablePanel>
          <div className="p-4">
            <form onSubmit={handleSavePrice} className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Exam Type
                </label>
                <select
                  value={examType}
                  onChange={(e) => setExamType(e.target.value)}
                  className="h-10 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#16213A]"
                >
                  {PRICE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  PIN Price (₦)
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  placeholder="e.g. 1000"
                  className="h-10 w-40 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#16213A]"
                />
              </div>
              <button type="submit" disabled={priceSaving} className={adminPrimaryButton}>
                {priceSaving ? "Saving..." : "Save Price"}
              </button>
            </form>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {PRICE_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setExamType(t.value)}
                  className={`rounded-lg border px-3 py-2 text-left transition ${
                    examType === t.value
                      ? "border-[#16213A] bg-[#16213A]/5"
                      : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                  }`}
                >
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t.label}
                  </div>
                  <div className="text-lg font-bold text-slate-900">
                    {prices[t.value] !== undefined && prices[t.value] !== ""
                      ? `₦${prices[t.value]}`
                      : "--"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </AdminTablePanel>

        {/* Pin management */}
        <AdminSectionHeader
          title="Manage Pins"
          description="Remove a PIN from the inventory."
          count={`${examPins.length} shown`}
        />

        {examPinsLoading ? (
          <AdminLoadingState />
        ) : examPins.length === 0 ? (
          <AdminEmptyState title="No pins" description="No exam pins to manage." />
        ) : (
          <AdminTablePanel>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">PIN</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">
                      Exam Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">
                      Created
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {examPins.map((pin) => (
                    <tr key={pin.id} className="border-b border-slate-100">
                      <td className="px-4 py-2">
                        <code className="text-sm">{pinLabel(pin)}</code>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`badge ${examTypeBadgeClass(examTypeLabel(pin))}`}>
                          {examTypeLabel(pin)}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`badge ${statusBadgeClass(statusLabel(pin))}`}>
                          {statusLabel(pin)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-slate-500">
                        {formatDate(pin.createdAt)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => handleDelete(pin)}
                          disabled={examPinMutationLoading}
                          className="text-sm font-semibold text-red-600 hover:text-red-800 disabled:opacity-40"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AdminTablePanel>
        )}
      </AdminPageBody>
    </div>
  );
};

export default ExamPinPrice;
